import moment from 'moment';

export class NonTimezoneSensitiveDate {
  private _year: number;
  private _month: number;
  private _date: number;

  /**
   * A local implementation to handle dates without time sensitivity
   * @param {number} year - complete full year (2024)
   * @param {number} month - one indexed month (1-12)
   * @param {number} date - one indexed date (1-31)
   */
  constructor(year: number, month: number, date: number) {
    this._year = year;
    this._month = month;
    this._date = date;
  }

  /**
   * Converts NonTimeSensitiveDate to javascript native date object (local time)
   * @returns Javascript Native Date Object
   */
  toDate(): Date {
    if (!this._year || !this._month || !this._date) {
      throw new Error('NonTimeSensitiveDates is not correctly initialized.');
    }
    return new Date(this._year, this._month - 1, this._date);
  }

  /**
   * Returns the current date in NonTimezoneSensitiveDate format
   * @returns NonTimezoneSensitiveDate
   */
  static now(): NonTimezoneSensitiveDate {
    return NonTimezoneSensitiveDate.fromNativeDate(new Date());
  }

  /**
   *  Returns ISO 8601 time insensitive string
   * @returns ISO 8601 string
   */
  toISO8601String(): string {
    if (!this._year || !this._date || !this._month) {
      throw new Error('NonTimeSensitiveDates is not correctly initialized.');
    }
    return `${this._year}-${String(this._month).padStart(2, '0')}-${String(this._date).padStart(2, '0')}`;
  }

  /**
   * Converts javascript date object to NonTimeSensitiveDate
   * @param {Date} date - Javascript default date object
   * @returns NonTimezoneSensitiveDate
   */
  static fromNativeDate(date: Date): NonTimezoneSensitiveDate {
    const localYear = date.getFullYear();
    const localMonth = date.getMonth() + 1; // getMonth is zero-indexed
    const localDate = date.getDate();
    return new NonTimezoneSensitiveDate(localYear, localMonth, localDate);
  }

  static fromDateTimeString(
    dateTimeString: string
  ): NonTimezoneSensitiveDate | null {
    if (!dateTimeString) return null;

    const datePart = dateTimeString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new NonTimezoneSensitiveDate(year, month + 1, day);
  }

  static fromISO8601String(dateString: string): NonTimezoneSensitiveDate {
    const [year, month, day] = dateString.split('-').map(Number);
    return new NonTimezoneSensitiveDate(year, month, day);
  }

  /**
   * A layer on top of moment's internal add function.
   * @param {number} step - How much you want to add to the current date
   * @param {string} period - The step size, whether you want to add a day, month or year
   * @returns NonTimezoneSensitiveDate
   */
  add(
    step: number,
    period: 'days' | 'months' | 'years' | 'weeks'
  ): NonTimezoneSensitiveDate {
    const updatedDate = moment(this.toDate()).add(step, period).toDate();
    return NonTimezoneSensitiveDate.fromNativeDate(updatedDate);
  }

  /**
   * A layer on top of moment's internal subtract function.
   * @param {number} step - How much you want to subtract from the current date
   * @param {string} period - The step size, whether you want to subtract a day, month or year
   * @returns NonTimezoneSensitiveDate
   */
  subtract(
    step: number,
    period: 'days' | 'month' | 'year' | 'hours' | 'weeks'
  ): NonTimezoneSensitiveDate {
    const updatedDate = moment(this.toDate()).subtract(step, period).toDate();
    return NonTimezoneSensitiveDate.fromNativeDate(updatedDate);
  }

  /**
   * Update the date to a particular date.
   * @param {number} date 1-31 integer
   * @returns NonTimezoneSensitiveDate
   */
  setDate(date: number): NonTimezoneSensitiveDate {
    return new NonTimezoneSensitiveDate(this._year, this._month, date);
  }

  /**
   * Update the month to a particular month.
   * @param {number} month 1-12 integer
   * @returns NonTimezoneSensitiveDate
   */
  setMonth(month: number): NonTimezoneSensitiveDate {
    return new NonTimezoneSensitiveDate(this._year, month, this._date);
  }

  /**
   * Update the year to a particular year.
   * @param {number} year YYYY integer
   * @returns NonTimezoneSensitiveDate
   */
  setYear(year: number): NonTimezoneSensitiveDate {
    return new NonTimezoneSensitiveDate(year, this._month, this._date);
  }

  toString(): string {
    return this.toDate() ? this.toDate().toString() : 'Invalid Date';
  }

  /**
   * Format the date to a human readable format.
   * @returns string
   * @example '15 Sept 2023'
   */
  toReadableString(): string {
    return this.toDate()
      ? moment(this.toDate()).format('MMMM D, YYYY')
      : 'Invalid Date';
  }

  getYear(): number {
    return this._year;
  }

  getMonth(): number {
    return this._month;
  }

  getDate(): number {
    return this._date;
  }

  // A layer on top of moment js's endOf function
  endOf(unitOfTime: 'year' | 'month'): NonTimezoneSensitiveDate {
    const updatedDate = moment(this.toDate()).endOf(unitOfTime).toDate();
    return NonTimezoneSensitiveDate.fromNativeDate(updatedDate);
  }

  startOf(unitOfTime: 'year' | 'month'): NonTimezoneSensitiveDate {
    const updatedDate = moment(this.toDate()).startOf(unitOfTime).toDate();
    return NonTimezoneSensitiveDate.fromNativeDate(updatedDate);
  }

  /**
   * Calculate the difference between two dates
   * @param {NonTimezoneSensitiveDate} date : The date to compare the current date with
   * @returns The difference between two dates in milliseconds
   */
  diff(date: NonTimezoneSensitiveDate): number {
    return moment(this.toDate()).diff(moment(date.toDate()));
  }

  /**
   * Calculate the difference between two dates in a specific unit
   * @param {NonTimezoneSensitiveDate} date : The date to compare the current date with
   * @param {string} as : The unit to return the difference in
   * @returns The difference between two dates in the required format
   */
  diffAs(
    date: NonTimezoneSensitiveDate,
    as: 'days' | 'hours' | 'minutes' | 'weeks' | 'months'
  ): number {
    const diff = moment.duration(this.diff(date));
    switch (as) {
      case 'days':
        return diff.asDays();
      case 'hours':
        return diff.asHours();
      case 'minutes':
        return diff.asMinutes();
      case 'weeks':
        return diff.asWeeks();
      case 'months':
        return diff.asMonths();
      default:
        // If the as input is wrong on run time simply return the diff in milliseconds
        return this.diff(date);
    }
  }

  // Check if the date is greater than the date you're comparing with
  gt(date: NonTimezoneSensitiveDate): boolean {
    return this.toDate() > date.toDate();
  }

  gte(date: NonTimezoneSensitiveDate): boolean {
    return this.toDate() >= date.toDate();
  }

  lt(date: NonTimezoneSensitiveDate): boolean {
    return this.toDate() < date.toDate();
  }

  lte(date: NonTimezoneSensitiveDate): boolean {
    return this.toDate() <= date.toDate();
  }

  // Get the short form month name from a month number
  getMonthName(): string {
    const allMonths = moment.monthsShort();
    return allMonths[this._month - 1];
  }

  // Wrapper around moment.js format string:
  format(formatString: string): string {
    return moment(this.toDate()).format(formatString);
  }

  static getMonthNames(): string[] {
    return moment.months();
  }

  /**
   * TODO: remove this after we migrate DB to fiscal year end. TEMP FIX.
   * Converts fiscal year start date/month to end date/month
   * @param {number} fiscalYearStartDay - Day of the month (1-31)
   * @param {number} fiscalYearStartMonth - Month (1-12)
   * @returns Object containing fiscal year end date and month
   */
  static convertFiscalYearStartToEnd(
    fiscalYearStartDay: number,
    fiscalYearStartMonth: number
  ): {
    fiscalYearEndDate: number;
    fiscalYearEndMonth: number;
  } {
    // Use current year just to calculate the end date (year doesn't matter)
    const currentYear = 2025;
    const startDate = new NonTimezoneSensitiveDate(
      currentYear,
      fiscalYearStartMonth,
      fiscalYearStartDay
    );

    // Add 1 year and subtract 1 day to get the end date
    const endDate = startDate.subtract(1, 'days');

    return {
      fiscalYearEndDate: endDate.getDate(),
      fiscalYearEndMonth: endDate.getMonth(),
    };
  }

  /**
   * The display label for a fiscal year end, taken from the year's *start*.
   *
   * `fiscalYearMonth`/`fiscalYearDay` store the start, so a December 31 year end is held as 1/1.
   * Formatting those directly prints the day the year opens under a label that says it closes.
   */
  static formatFiscalYearEndFromStart(
    startMonth?: number | null,
    startDay?: number | null
  ): string | null {
    if (!startMonth || !startDay) return null;

    // Note the argument order: the converter takes the day first, then the month.
    const { fiscalYearEndDate, fiscalYearEndMonth } =
      NonTimezoneSensitiveDate.convertFiscalYearStartToEnd(startDay, startMonth);
    const monthName =
      NonTimezoneSensitiveDate.getMonthNames()[fiscalYearEndMonth - 1];

    return monthName ? `${monthName} ${fiscalYearEndDate}` : null;
  }

  /**
   * TODO: remove this after we migrate DB to fiscal year end. TEMP FIX.
   * Converts fiscal year start date/month to end date/month
   * @param {number} fiscalYearStartDay - Day of the month (1-31)
   * @param {number} fiscalYearStartMonth - Month (1-12)
   * @returns Object containing fiscal year end date and month
   */
  static convertFiscalYearEndToStart(
    fiscalYearEndDay: number,
    fiscalYearEndMonth: number
  ): {
    fiscalYearStartDate: number;
    fiscalYearStartMonth: number;
  } {
    // Use current year just to calculate the end date (year doesn't matter)
    const currentYear = 2025;
    const endDate = new NonTimezoneSensitiveDate(
      currentYear,
      fiscalYearEndMonth,
      fiscalYearEndDay
    );

    // Add 1 year and subtract 1 day to get the end date
    const startDate = endDate.add(1, 'days');

    return {
      fiscalYearStartDate: startDate.getDate(),
      fiscalYearStartMonth: startDate.getMonth(),
    };
  }
}

/** Returns the latest of the given ISO-8601 date strings, skipping nullish entries; undefined if none. */
export function getLatestDateString(
  dates: readonly (string | null | undefined)[]
): string | undefined {
  return dates.reduce<string | undefined>((latest, date) => {
    if (!date) return latest;
    if (!latest) return date;
    return NonTimezoneSensitiveDate.fromISO8601String(date).lte(
      NonTimezoneSensitiveDate.fromISO8601String(latest)
    )
      ? latest
      : date;
  }, undefined);
}
